import { NgModule } from '@angular/core';
import { Routes, RouterModule, RouteReuseStrategy } from '@angular/router';
import { AuthService } from './services/auth.service';
import { CONFIGURATION_LEGACY_REDIRECTS } from './configuration/configuration-registry';
import { TenantRouteReuseStrategy } from './shared/services/tenant-route-reuse.strategy';
import { AuthCallbackComponent } from './auth-callback/auth-callback.component';
import { AuthSignoutCallbackComponent } from './auth-callback/auth-signout-callback';
import { SilentRefreshComponent } from './silent-refresh/silent-refresh.component';
import { NotFoundComponent } from './error-pages/not-found/not-found.component';
import { UnauthorizedComponent } from './unauthorized/unauthorized.component';
import { RegistrationComponent } from './registration/registration.component';
import { canActivateIssues, canActivateLandingPage, canActivateServiceCenter } from './services/permission.service';
import { canActivateThirdPartyApp } from './links/links.guard';
import { TenantFeatures } from './models/featureSettings';

const routes: Routes = [

  {
    path: 'auth-callback',
    component: AuthCallbackComponent,
  },
  {
    path: 'auth-signout-callback',
    component: AuthSignoutCallbackComponent,
  },
  {
    path: 'registration',
    redirectTo: 'expired/registration'
  },
  {
    path: 'expired/registration',
    component: RegistrationComponent
  },
  {
    path: ':tenantId/registration',
    component: RegistrationComponent
  },
  {
    path: 'silent-refresh.html',
    redirectTo: 'silent-refresh'
  },
  {
    path: 'silent-refresh',
    component: SilentRefreshComponent,
    pathMatch: 'full',
  },
  { path: '404', component: NotFoundComponent },
  { path: 'unauthorized', component: UnauthorizedComponent },

  // Old setup-screen paths → Configuration (see configuration-registry).
  ...CONFIGURATION_LEGACY_REDIRECTS,

  // ===== New routes: :tenantId as parent =====
  {
    path: ':tenantId',
    children: [
      {
        path: 'configuration',
        loadChildren: () =>
          import('./configuration/configuration.module')
            .then(m => m.ConfigurationModule)
      },
      {
        path: 'globalAdmin',
        loadChildren: () =>
          import('./enum-definitions/enum-definitions.module')
            .then(m => m.EnumDefinitionsModule)
      },
      {
        path: 'dashboard',
        loadChildren: () =>
          import('./dashboard/dashboard.module')
            .then(m => m.DashboardModule)
      },
      {
        path: 'diagnostics',
        loadChildren: () =>
          import('./diagnostics/diagnostics.module')
            .then(m => m.DiagnosticsModule)
      },
      {
        path: 'fleet',
        loadChildren: () =>
          import('./fleet/fleet.module')
            .then(m => m.FleetModule)
      },
      {
        path: 'work',
        loadChildren: () =>
          import('./work/work.module')
            .then(m => m.WorkModule)
      },
      {
        path: 'profile',
        loadChildren: () =>
          import('./profile/profile.module')
            .then(m => m.ProfileModule)
      },
      {
        path: 'motor',
        data: { feature: TenantFeatures.FleetCross, hideSidebar: true },
        canActivate: [canActivateThirdPartyApp],
        loadChildren: () =>
          import('./motortab/motortab.module')
            .then(m => m.MotortabModule)
      },
      {
        path: 'mitchell1',
        data: { feature: TenantFeatures.Mitchell1, hideSidebar: true },
        canActivate: [canActivateThirdPartyApp],
        loadChildren: () =>
          import('./mitchell1/mitchell1.module')
            .then(m => m.Mitchell1Module)
      },
      {
        path: 'oos',
        loadChildren: () =>
          import('./out-of-service/out-of-service.module')
            .then(m => m.OutOfServiceModule)
      },
      {
        path: 'jaltest',
        data: { feature: TenantFeatures.Jaltest, hideSidebar: true },
        canActivate: [canActivateThirdPartyApp],
        loadChildren: () =>
          import('./jaltest/jaltest.module')
            .then(m => m.JaltestModule)
      },
      {
        path: 'issues',
        // No sidebar. The sheet carries its own grouping and saved views, so a left nav
        // would list screens that no longer exist.
        data: { hideSidebar: true },
        canActivate: [canActivateIssues],
        loadChildren: () =>
          import('./issues/issues-area.module')
            .then(m => m.IssuesAreaModule)
      },
      {
        path: 'serviceCenter',
        data: { hideSidebar: true },
        canActivate: [canActivateServiceCenter],
        loadChildren: () =>
          import('./service-center/service-center.module')
            .then(m => m.ServiceCenterModule)
      },
      {
        // silent-refresh/404/unauthorized must stay above :tenantId or this child swallows them.
        path: '',
        pathMatch: 'full',
        canActivate: [canActivateLandingPage],
        children: []
      },
    ]
  },

  // ===== Legacy redirects: old routes → new :tenantId-first routes =====
  // Explicit catches for the notification-link shapes emitted by backend
  // subscribers (fleet-hd-subscriber-workitem-data-function, fleet-hd-alert-
  // service, fleet-hd-subscriber-schedule-function, etc). `:tenantId` matches
  // the full externalTenantId (including the "ext-" prefix) verbatim.
  { path: 'diagnostics/alertdetail/:tenantId/:alertId', redirectTo: ':tenantId/diagnostics/alert/:alertId' },
  { path: 'work/workItem/:tenantId/:type/:id', redirectTo: ':tenantId/work/workItem/:type/:id' },
  { path: 'fleet/vehicledetail/:tenantId/:vin', redirectTo: ':tenantId/fleet/vehicle/:vin' },
  { path: 'fleet/vehicle/:tenantId/:vin', redirectTo: ':tenantId/fleet/vehicle/:vin' },

  // Generic fallbacks: module/:child/:tenantId/... → :tenantId/module/:child/...
  { path: 'fleet/:child/:tenantId', redirectTo: ':tenantId/fleet/:child', pathMatch: 'prefix' },
  { path: 'diagnostics/:child/:tenantId', redirectTo: ':tenantId/diagnostics/:child', pathMatch: 'prefix' },
  { path: 'dashboard/:child/:tenantId', redirectTo: ':tenantId/dashboard/:child', pathMatch: 'prefix' },
  { path: 'work/:child/:tenantId', redirectTo: ':tenantId/work/:child', pathMatch: 'prefix' },
  { path: 'oos/:child/:tenantId', redirectTo: ':tenantId/oos/:child', pathMatch: 'prefix' },
  { path: 'motor/:child/:tenantId', redirectTo: ':tenantId/motor/:child', pathMatch: 'prefix' },
  { path: 'mitchell1/:child/:tenantId', redirectTo: ':tenantId/mitchell1/:child', pathMatch: 'prefix' },
  { path: 'jaltest/:child/:tenantId', redirectTo: ':tenantId/jaltest/:child', pathMatch: 'prefix' },
  // Profile exception: module/:tenantId/:child → :tenantId/module/:child
  { path: 'profile/:tenantId', redirectTo: ':tenantId/profile', pathMatch: 'prefix' },

  {
    // Landing route: the guard always redirects (navigateToLanding) or starts
    // auth, returning false, so this route never renders a view. It still needs
    // one of component/children/redirectTo/loadChildren to be valid config —
    // an empty `children` satisfies that without rendering anything. (Prod mode
    // skips route validation, so a bare guard-only route silently "worked"
    // there; dev mode rejects it with NG04014 and aborts bootstrap.)
    path: '',
    canActivate: [canActivateLandingPage],
    pathMatch: 'full',
    children: []
  },
];

@NgModule({
  imports: [RouterModule, RouterModule.forRoot(routes, { paramsInheritanceStrategy: 'always' })],
  exports: [RouterModule],
  providers: [AuthService, { provide: RouteReuseStrategy, useClass: TenantRouteReuseStrategy }],
})
export class AppRoutingModule { }
